import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { exportSipProduct, fetchSipTree } from '../../api/client';
import type { SipTreeCustomer, SipTreeNode, SipTreeLeaf } from '../../types';

/**
 * 「作业指导书」顶栏入口的 SIP 选择器（v0.90，沈哥 2026-10-24 重设计）。
 *
 * 沈哥口径：选图层级**不要图纸这一层**，直接落到 SIP。
 * 所以这里是一棵「客户 → 产品(成品) → [半成品] → SIP」的物料树：
 *   · 客户 / 产品 / 半成品 来自 BOM 镜像 `bom_part_ref`；
 *   · 半成品经 `bom_child_ref` 挂在父成品下面（共用件按各父产品重复挂）；
 *   · 叶子是「一份 SIP」（原材料/制程/成品/出货/自检/测量报告/工序 SIP），
 *     点叶子直接打开那一份 —— 同产品多份 SIP 共用一张气泡图，图纸层不单列。
 *   · 非成品/半成品（原材料 / 未归户图纸）不进树，靠顶部搜索框触达。
 *
 * ---------------------------------------------------------------------------
 * 与旧版（文件夹树）刻意的取舍
 * ---------------------------------------------------------------------------
 * 1. **数据源换成 `['sip-tree']`**（`GET /api/sip/tree`），不再复用图纸区的
 *    `['folders']` 缓存 —— 旧版想"和图纸区联动"，但沈哥明确要的是按物料维度，
 *    两者语义已经不同，硬共用反而把图纸分类带进来。
 * 2. **折叠态默认全开**（反集合表示，见下）—— 进来一眼看全，符合"挑一份就走"。
 * 3. **顶部带搜索框**：覆盖树里不显示的未归户/原材料类图纸（服务端 `?q=`）。
 */

interface Props {
  /** 当前打开的那份 SIP 的指纹（用于高亮）：`drawing:kind:seq:op`。 */
  currentKey?: string | null;
  /** 点中一份 SIP（叶子）。 */
  onPick: (sel: SipTreeLeaf) => void;
  /** 轻提示（可选；不传就静默）。 */
  onToast?: (msg: string, kind?: 'success' | 'error' | 'info') => void;
}

export function leafKey(l: SipTreeLeaf): string {
  return `${l.drawing_id}:${l.doc_kind}:${l.seq}:${l.route_op_id}`;
}

function leafStatus(l: SipTreeLeaf): string {
  if (!l.has_draft) return '未存草稿';
  return l.item_count > 0 ? `已存 ${l.item_count} 行` : '未编辑明细';
}

export function SipDrawingPicker({ currentKey, onPick, onToast }: Props) {
  const [q, setQ] = useState('');
  /** 正在打包导出的物料编码（防止重复点 + 显示转圈）。 */
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const treeQ = useQuery({
    queryKey: ['sip-tree', ''],
    queryFn: () => fetchSipTree(''),
    refetchOnWindowFocus: false,
  });
  const searching = q.trim().length > 0;
  const searchQ = useQuery({
    queryKey: ['sip-tree', q],
    queryFn: () => fetchSipTree(q),
    enabled: searching,
    refetchOnWindowFocus: false,
  });

  /** 收起的那几个（用"反集合"表示默认全开）。 */
  const [closed, setClosed] = useState<Set<string>>(new Set());
  const toggle = (k: string) => setClosed((p) => {
    const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  const data = searching ? searchQ.data : treeQ.data;

  const Leaf = ({ l, depth }: { l: SipTreeLeaf; depth: number }) => (
    <button className={`sip-pk-leaf${currentKey === leafKey(l) ? ' on' : ''}`}
            style={{ paddingLeft: 10 + depth * 16 }}
            title={`${l.label} · ${leafStatus(l)}`}
            data-sip-pick-leaf={leafKey(l)}
            onClick={() => onPick(l)}>
      <span className="sip-pk-leaf-no">{l.is_route_op ? 'OP' : l.doc_kind}</span>
      <span className="sip-pk-leaf-label">{l.name}</span>
      {l.drawing_label && <span className="sip-pk-leaf-dwg">{l.drawing_label}</span>}
      <span className="sip-pk-leaf-n">{leafStatus(l)}</span>
      {l.has_draft && <em className="sip-pk-badge">草稿</em>}
    </button>
  );

  /** 一键导出该物料（产品/半成品）名下所有图纸的全部 SIP 为一个 zip。 */
  const handleExportProduct = async (n: SipTreeNode) => {
    if (busyCode) return;
    setBusyCode(n.code);
    try {
      const r = await exportSipProduct(n.code, true);
      onToast?.(`已导出：${n.name} 的全部 SIP（${r.ok} 份`
                + (r.skipped ? `，跳过 ${r.skipped} 份空表` : '')
                + (r.failed ? `，${r.failed} 份失败` : '')
                + `，用时 ${r.elapsed}s）`, 'success');
    } catch (e: any) {
      onToast?.(`导出失败：${e?.message || e}`, 'error');
    } finally {
      setBusyCode(null);
    }
  };

  const Node = ({ n, depth }: { n: SipTreeNode; depth: number }) => {
    const k = `${n.type}:${n.code}`;
    const isClosed = closed.has(k);
    const hasKids = Object.keys(n.children).length > 0 || n.sips.length > 0;
    const isBusy = busyCode === n.code;
    return (
      <>
        <div className={`sip-pk-folder${hasKids ? '' : ' leaf'}${n.type === 'semi' ? ' semi' : ''}`}
             style={{ paddingLeft: 4 + depth * 16 }}
             role="button" tabIndex={0}
             title={`${n.name}（${n.type === 'semi' ? '半成品' : '成品'}）· ${n.drawing_count} 张图 · ${n.sips.length} 份 SIP`}
             onClick={() => hasKids && toggle(k)}
             onKeyDown={(e) => {
               if (e.key === 'Enter' || e.key === ' ') {
                 e.preventDefault(); if (hasKids) toggle(k);
               }
             }}>
          <span className="tw">{hasKids ? (isClosed ? '▸' : '▾') : '·'}</span>
          <span className="fi">{n.type === 'semi' ? '🔧' : '📦'}</span>
          <span className="nm">{n.name}</span>
          <span className="ct">{n.type === 'semi' ? '半成品' : '成品'}</span>
          <button className="sip-pk-exp"
                  title="一键导出该产品的所有 SIP（zip）"
                  disabled={!!busyCode}
                  onClick={(e) => { e.stopPropagation(); handleExportProduct(n); }}>
            {isBusy ? '导出中…' : '⬇ 导出全部 SIP'}
          </button>
        </div>
        {!isClosed && (
          <>
            {n.sips.map((l) => <Leaf key={leafKey(l)} l={l} depth={depth + 1} />)}
            {Object.entries(n.children).map(([ck, c]) => (
              <Node key={ck} n={c} depth={depth + 1} />
            ))}
          </>
        )}
      </>
    );
  };

  return (
    <div className="sip-pick" data-sip-pick-tree="1">
      <div className="sip-pick-title">按「客户 → 产品 → SIP」选一份作业指导书</div>

      <div className="sip-pk-search">
        <input
          className="sip-pk-search-input"
          placeholder="搜索：客户 / 产品 / 物料编码 / 文件名"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {!searching && treeQ.isLoading && <div className="hint">加载中…</div>}
      {searching && searchQ.isLoading && <div className="hint">搜索中…</div>}
      {((!searching && treeQ.isError) || (searching && searchQ.isError)) && (
        <div className="hint">加载失败，请重试</div>
      )}

      {data && data.mode === 'tree' && (
        <div className="sip-pick-list">
          {data.customers!.map((c: SipTreeCustomer) => (
            <div key={c.name} className="sip-pk-customer">
              <div className="sip-pk-customer-name">🏢 {c.name}</div>
              {Object.entries(c.nodes).map(([nk, n]) => (
                <Node key={nk} n={n} depth={0} />
              ))}
            </div>
          ))}
          {data.customers!.length === 0 && (
            <div className="hint">库里还没有归户到成品 / 半成品的图纸</div>
          )}
        </div>
      )}

      {data && data.mode === 'search' && (
        <div className="sip-pick-list">
          {(data.matches || []).map((l) => (
            <div key={leafKey(l)}>
              {l.breadcrumb && <div className="sip-pk-breadcrumb">{l.breadcrumb}</div>}
              <Leaf l={l} depth={0} />
            </div>
          ))}
          {(data.matches || []).length === 0 && (
            <div className="hint">没搜到匹配的图纸</div>
          )}
        </div>
      )}
    </div>
  );
}
